/**
 * Utility functions for generating and handling SEO-friendly slugs
 */

/**
 * Generates a URL-friendly slug from a product name
 * @param {string} productName - The product name to convert to a slug
 * @returns {string} - URL-friendly slug
 * 
 * Examples:
 * - "Cetaphil Gentle Skin Cleanser" -> "cetaphil-gentle-skin-cleanser"
 * - "La Roche-Posay Anthelios 50+ Sunscreen" -> "la-roche-posay-anthelios-50-sunscreen"
 * - "CeraVe Moisturizing Cream (16 oz)" -> "cerave-moisturizing-cream-16-oz"
 */
export const generateProductSlug = (productName) => {
  if (!productName || typeof productName !== 'string') {
    return '';
  }

  return productName
    .toLowerCase() // Convert to lowercase
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens and spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generates a slug with brand and product name for better SEO
 * @param {string} brand - The product brand
 * @param {string} productName - The product name
 * @returns {string} - URL-friendly slug with brand
 * 
 * Example:
 * - ("Cetaphil", "Gentle Skin Cleanser") -> "cetaphil-gentle-skin-cleanser"
 */
export const generateBrandedProductSlug = (brand, productName) => {
  if (!productName) return '';
  
  // If product name already includes brand, don't duplicate it
  const lowerProductName = productName.toLowerCase();
  const lowerBrand = (brand || '').toLowerCase();
  
  if (lowerProductName.startsWith(lowerBrand)) {
    return generateProductSlug(productName);
  }
  
  // Combine brand and product name
  const combined = brand ? `${brand} ${productName}` : productName;
  return generateProductSlug(combined);
};

/**
 * Generates a category-based slug
 * @param {string} categoryName - The category name
 * @returns {string} - URL-friendly category slug
 */
export const generateCategorySlug = (categoryName) => {
  return generateProductSlug(categoryName);
};

/**
 * Generates a subcategory slug
 * @param {string} subcategoryName - The subcategory name
 * @returns {string} - URL-friendly subcategory slug
 */
export const generateSubcategorySlug = (subcategoryName) => {
  return generateProductSlug(subcategoryName);
};

/**
 * Generates a tag-based page slug
 * @param {string} tagName - The tag name
 * @returns {string} - URL-friendly tag slug
 */
export const generateTagSlug = (tagName) => {
  return generateProductSlug(tagName);
};

/**
 * Parses a slug back to a readable title (for display purposes)
 * @param {string} slug - The slug to parse
 * @returns {string} - Human-readable title
 * 
 * Example:
 * - "cetaphil-gentle-skin-cleanser" -> "Cetaphil Gentle Skin Cleanser"
 */
export const parseSlugToTitle = (slug) => {
  if (!slug || typeof slug !== 'string') {
    return '';
  }

  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};



